import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  message,
  Popconfirm,
  DatePicker,
  Select,
  Row,
  Col,
  Form,
} from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  CalculatorOutlined,
} from '@ant-design/icons';
import * as adminactions from '../../store/actions/AuthActions';
import useIsMobile from '../../hooks/useIsMobile';
import MobileFilterDrawer, { MobileFilterButton } from '../../components/Common/MobileFilterDrawer';
import useUrlFilters from 'hooks/useUrlFilters';

const { RangePicker } = DatePicker;

// Applied filters live in the URL so a filtered view can be shared as a link.
const URL_FILTERS = {
  employee: { type: 'string' },
  range: { type: 'dateRange', keys: ['date_from', 'date_to'] },
  status: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 10 },
};

const Attendance = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const reduxOrganization = useSelector((state) => state.auth.organization);
  const organization = process.env.REACT_APP_ORGANIZATION_UUID || reduxOrganization;
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [employees, setEmployees] = useState([]);
  const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS);
  const filters = useMemo(() => {
    const searchFilters = {};
    if (urlFilters.employee) {
      searchFilters.employee = urlFilters.employee;
    }
    if (urlFilters.range) {
      searchFilters.date_from = urlFilters.range[0].format('YYYY-MM-DD');
      searchFilters.date_to = urlFilters.range[1].format('YYYY-MM-DD');
    }
    if (urlFilters.status) {
      searchFilters.status = urlFilters.status;
    }
    return searchFilters;
  }, [urlFilters]);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [calculating, setCalculating] = useState(false);

  // Count active filters
  const getActiveFiltersCount = () => {
    const values = form.getFieldsValue();
    let count = 0;
    if (values.employee) count++;
    if (values.dateRange && values.dateRange.length === 2) count++;
    if (values.status) count++;
    return count;
  };

  // Load employees for filter dropdown
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await adminactions.loadEmployeesByOrganization(organization, 1, null, 1000);
        if (response && response.results) {
          setEmployees(response.results);
        }
      } catch (error) {
        console.error('Error loading employees:', error);
      }
    };

    if (organization) {
      fetchEmployees();
    }
  }, [organization]);

  // Load attendance records
  const loadAttendance = async (page = 1, pageSize = 10, searchFilters = {}) => {
    setLoading(true);
    try {
      const response = await adminactions.loadAttendanceRecords(
        page,
        pageSize,
        organization,
        searchFilters
      );

      if (response && response.results) {
        setDataSource(response.results);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.count,
        });
      }
    } catch (error) {
      message.error('Failed to load attendance records');
      console.error('Error loading attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (organization) {
      loadAttendance(urlFilters.page, Math.min(urlFilters.pageSize, MAX_PAGE_SIZE), filters);
    }
  }, [organization, urlFilters, filters]);

  // Keep the filter form in sync with the URL (shared links, back/forward).
  // Employee ids come back from the URL as strings, so match them to the option value.
  useEffect(() => {
    const employee = urlFilters.employee
      ? (employees.find((emp) => String(emp.id) === urlFilters.employee)?.id ?? urlFilters.employee)
      : undefined;
    form.setFieldsValue({
      employee,
      dateRange: urlFilters.range || undefined,
      status: urlFilters.status || undefined,
    });
  }, [urlFilters, employees]);

  // Handle table pagination change
  const handleTableChange = (newPagination) => {
    // A new page size starts again from page 1
    const sizeChanged = newPagination.pageSize !== urlFilters.pageSize;
    setUrlFilters({ page: sizeChanged ? 1 : newPagination.current, pageSize: newPagination.pageSize });
  };

  // Handle search/filter
  const handleSearch = () => {
    const values = form.getFieldsValue();
    setUrlFilters({
      employee: values.employee || null,
      range: values.dateRange && values.dateRange.length === 2 ? values.dateRange : null,
      status: values.status || null,
      page: 1,
    });
  };

  // Handle delete
  const handleDelete = async (id) => {
    try {
      await adminactions.deleteAttendance(id);
      message.success('Attendance record deleted successfully');
      loadAttendance(pagination.current, pagination.pageSize, filters);
    } catch (error) {
      message.error('Failed to delete attendance record');
      console.error('Error deleting attendance:', error);
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Employee',
      dataIndex: 'employee_name',
      key: 'employee',
      render: (text, record) => (
        <span
          style={{ color: '#1890ff', cursor: 'pointer' }}
          onClick={() => navigate(`/attendance/view/${record.id}`)}
        >
          {record.employee_name || '-'}
        </span>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: true,
    },
    {
      title: 'Check In',
      dataIndex: 'first_in',
      key: 'first_in',
      render: (text, record) => {
        if (!text) return '-';
        return `${record.date} ${text}`;
      },
    },
    {
      title: 'Check Out',
      dataIndex: 'last_out',
      key: 'last_out',
      render: (text, record) => {
        if (!text) return '-';
        return `${record.date} ${text}`;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusColors = {
          present: 'green',
          absent: 'red',
          leave: 'orange',
          holiday: 'blue',
        };
        return (
          <span style={{ color: statusColors[status] || 'black' }}>
            {status?.toUpperCase()}
          </span>
        );
      },
    },
    {
      title: 'Working Hours',
      dataIndex: 'total_hours_worked',
      key: 'total_hours_worked',
      render: (hours) => hours || '0',
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/attendance/view/${record.id}`)}
            title="View"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/attendance/edit/${record.id}`)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this record?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} title="Delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Clear filters
  const handleClearFilters = () => {
    form.resetFields();
    setUrlFilters({ employee: null, range: null, status: null, page: 1 });
  };

  // Calculate attendance from device logs
  const handleCalculateAttendance = async () => {
    setCalculating(true);
    try {
      const values = form.getFieldsValue();
      const calcFilters = {};

      if (values.employee) {
        calcFilters.employee = values.employee;
      }
      if (values.dateRange && values.dateRange.length === 2) {
        calcFilters.date_from = values.dateRange[0].format('YYYY-MM-DD');
        calcFilters.date_to = values.dateRange[1].format('YYYY-MM-DD');
      }

      const result = await adminactions.calculateAttendanceFromLogs(
        organization,
        calcFilters
      );

      if (result && result.success) {
        message.success(
          `Attendance calculated: ${result.attendance_created} created, ${result.attendance_updated} updated`
        );
        loadAttendance(1, pagination.pageSize, filters);
      } else {
        message.warning(result?.message || 'No attendance records were processed');
      }
    } catch (error) {
      console.error('Error calculating attendance:', error);
    } finally {
      setCalculating(false);
    }
  };

  // Filter form content
  const filterFormContent = (
    <Form form={form} layout="vertical">
      <Row gutter={16}>
        <Col xs={24} sm={8}>
          <Form.Item name="employee" label="Employee">
            <Select
              placeholder="Select Employee"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {employees.map((emp) => (
                <Select.Option key={emp.id} value={emp.id}>
                  {emp.user?.first_name} {emp.user?.last_name} ({emp.employee_id || 'N/A'})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item name="dateRange" label="Date Range">
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={isMobile ? 24 : 6}>
          <Form.Item name="status" label="Status">
            <Select placeholder="Select Status" allowClear>
              <Select.Option value="present">Present</Select.Option>
              <Select.Option value="absent">Absent</Select.Option>
              <Select.Option value="leave">Leave</Select.Option>
              <Select.Option value="holiday">Holiday</Select.Option>
              <Select.Option value="partial">Partial Day</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        {!isMobile && (
          <Col xs={24} sm={2} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Form.Item style={{ marginBottom: 24 }}>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
              >
                Search
              </Button>
            </Form.Item>
          </Col>
        )}
      </Row>
    </Form>
  );

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      <Card
        title={isMobile ? "Attendance" : "Attendance Management"}
        extra={
          <Space>
            {isMobile && (
              <MobileFilterButton
                onClick={() => setFilterDrawerVisible(true)}
                activeFiltersCount={getActiveFiltersCount()}
                size="small"
              />
            )}
            <Popconfirm
              title="Calculate Attendance"
              description="This will process device logs and create/update attendance records based on current filters. Continue?"
              onConfirm={handleCalculateAttendance}
              okText="Yes, Calculate"
              cancelText="Cancel"
            >
              <Button
                icon={<CalculatorOutlined />}
                loading={calculating}
                size={isMobile ? 'small' : 'middle'}
              >
                {isMobile ? 'Calculate' : 'Calculate Attendance'}
              </Button>
            </Popconfirm>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/attendance/add')}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? 'Add' : 'Add Attendance'}
            </Button>
          </Space>
        }
        size={isMobile ? 'small' : 'default'}
      >
        {/* Desktop Filters */}
        {!isMobile && (
          <div style={{ marginBottom: 16 }}>
            {filterFormContent}
          </div>
        )}

        {/* Mobile Filter Drawer */}
        {isMobile && (
          <MobileFilterDrawer
            visible={filterDrawerVisible}
            onClose={() => setFilterDrawerVisible(false)}
            onApply={handleSearch}
            onClear={handleClearFilters}
            title="Attendance Filters"
            activeFiltersCount={getActiveFiltersCount()}
          >
            {filterFormContent}
          </MobileFilterDrawer>
        )}

        {/* Table */}
        <Table
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            size: isMobile ? 'small' : 'default',
            showSizeChanger: !isMobile,
            simple: isMobile,
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size={isMobile ? 'small' : 'middle'}
        />
      </Card>
    </div>
  );
};

export default Attendance;
